/**
 * Shape of builder `Language` rows used here — defined locally so `_default` can publish
 * without importing `src/store` (store `Language` is assignable to this).
 */
export type LanguageLike = {
  id?: number;
  /** V2 `/languageSettings` flat row */
  name?: string;
  icon?: string;
  languageCode?: string;
  locale?: string;
  languageShortname?: string;
  order?: number;
  manager_state?: number;
  visitor_state?: number;
  visitorState?: number;
  language?: {
    id?: number;
    name?: string;
    shortname?: string;
    icon?: string;
    icon_url?: string;
  } | null;
};

/** Published V2 store uses camelCase; builder normalize may use snake_case. */
export type TranslationTargetLike = {
  target_webpage_uuid?: string;
  targetWebpageUuid?: string;
  target_webpage_id?: number;
  targetWebpageId?: number;
};

export function translationTargetUuid(
  translation: TranslationTargetLike | null | undefined
): string {
  if (!translation) return "";
  const uuid =
    translation.target_webpage_uuid ?? translation.targetWebpageUuid ?? "";
  return typeof uuid === "string" ? uuid.trim() : "";
}

export function translationTargetWebpageId(
  translation: TranslationTargetLike | null | undefined
): number | undefined {
  if (!translation) return undefined;
  const id = translation.target_webpage_id ?? translation.targetWebpageId;
  return typeof id === "number" && Number.isFinite(id) ? id : undefined;
}

/** 1 = visible for visitors, 0 = admin only. Accepts V2 camelCase or snake_case. */
export function isVisitorVisibleLanguage(
  language: LanguageLike | null | undefined
): boolean {
  return Number(language?.visitor_state ?? language?.visitorState ?? 0) === 1;
}

export function visitorStateFromLanguage(
  language: LanguageLike | null | undefined
): number {
  const raw = language?.visitor_state ?? language?.visitorState ?? 0;
  return Number(raw) === 1 ? 1 : 0;
}

/** Lowercase trimmed form for comparisons only. */
export function normalizeLanguageCode(
  code: string | null | undefined
): string {
  return String(code ?? "")
    .trim()
    .toLowerCase();
}

export function languageCodesEqual(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  return normalizeLanguageCode(a) === normalizeLanguageCode(b);
}

/** Canonical code string for a site language row (for matching to webpage.languageCode). */
export function languageCodeFromActiveLanguage(
  lang: LanguageLike | null | undefined
): string {
  if (!lang) return "";
  const top =
    (typeof lang.languageCode === "string" && lang.languageCode.trim()) ||
    (typeof lang.locale === "string" && lang.locale.trim()) ||
    (typeof lang.languageShortname === "string" &&
      lang.languageShortname.trim()) ||
    "";
  if (top) return top;
  const short = lang.language?.shortname;
  return typeof short === "string" ? short : "";
}

/**
 * URL path segment for routes — mirrors `WebsiteLanguage::routeSegment()` /
 * `Webpage::getPageLocaleUrl()` (shortname when set, else locale / languageCode).
 */
export function routeSegmentFromActiveLanguage(
  lang: LanguageLike | null | undefined
): string {
  if (!lang) return "";
  const short =
    typeof lang.language?.shortname === "string"
      ? lang.language.shortname.trim()
      : "";
  if (short) return short;
  return languageCodeFromActiveLanguage(lang).trim();
}

/**
 * First language in builder order (`activeLanguages` is already sorted by `order`).
 * When `visitorOnly` is true, skips admin-only rows and falls back to the full list.
 */
export function defaultLanguageCodeFromActiveLanguages(
  activeLanguages: LanguageLike[] | null | undefined,
  options: { visitorOnly?: boolean } = {}
): string {
  const list = activeLanguages ?? [];
  if (!list.length) {
    return "";
  }
  const { visitorOnly = false } = options;
  if (visitorOnly) {
    const visitorRows = list.filter((row) => isVisitorVisibleLanguage(row));
    if (visitorRows.length) {
      return languageCodeFromActiveLanguage(visitorRows[0]);
    }
  }
  return languageCodeFromActiveLanguage(list[0]);
}

/** Normalized locale codes configured on the website (languages + optional pages). */
export function configuredLanguageCodes(
  pageLanguages: LanguageLike[],
  pages?: Array<{ languageCode?: string }>
): string[] {
  const codes = new Set<string>();
  for (const lang of pageLanguages) {
    const code = normalizeLanguageCode(languageCodeFromActiveLanguage(lang));
    if (code) codes.add(code);
    const segment = normalizeLanguageCode(routeSegmentFromActiveLanguage(lang));
    if (segment) codes.add(segment);
  }
  pages?.forEach((page) => {
    const code = normalizeLanguageCode(page.languageCode);
    if (code) codes.add(code);
  });
  return [...codes];
}

/**
 * When pathname is a single segment (`/de`, `/fi`), returns that segment if it
 * matches a configured site language; otherwise null.
 */
export function localeSegmentFromSingleSegmentPath(
  pathname: string,
  pageLanguages: LanguageLike[],
  pages?: Array<{ languageCode?: string }>
): string | null {
  const normalized =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  const parts = normalized.split("/").filter(Boolean);
  if (parts.length !== 1) {
    return null;
  }
  const segment = parts[0];
  const known = configuredLanguageCodes(pageLanguages, pages);
  if (!known.some((code) => languageCodesEqual(code, segment))) {
    return null;
  }
  return segment;
}

type PageWithLanguageCode = { languageCode?: string; pageType?: string };

/** Pick a homepage following builder language order (first matching `languageCode`). */
export function selectHomepageByLanguageOrder<T extends PageWithLanguageCode>(
  homepages: T[],
  orderedLanguages: LanguageLike[]
): T | undefined {
  for (const lang of orderedLanguages) {
    const code = languageCodeFromActiveLanguage(lang);
    if (!code) continue;
    const page = homepages.find((row) =>
      languageCodesEqual(row.languageCode, code)
    );
    if (page) {
      return page;
    }
  }
  return homepages[0];
}

/** UI label: V2 `name`, nested `language.name`, else canonical code. */
export function displayNameFromActiveLanguage(
  lang: LanguageLike | null | undefined
): string {
  if (!lang) return "";
  if (typeof lang.name === "string") {
    const t = lang.name.trim();
    if (t) return t;
  }
  const nested = lang.language?.name;
  if (typeof nested === "string" && nested.trim()) return nested.trim();
  return languageCodeFromActiveLanguage(lang);
}

/** Estonian site languages: `et`, `et_EE`, etc. */
export function isEstonianLanguageCode(
  code: string | null | undefined
): boolean {
  const n = normalizeLanguageCode(code).replace(/-/g, "_");
  if (n === "et") return true;
  return n.startsWith("et_");
}

/** Russian site languages: `ru`, `ru_RU`, etc. */
export function isRussianLanguageCode(
  code: string | null | undefined
): boolean {
  const n = normalizeLanguageCode(code).replace(/-/g, "_");
  if (n === "ru") return true;
  return n.startsWith("ru_");
}

/** English site languages: `en`, `en_GB`, `en-US`, etc. */
export function isEnglishLanguageCode(
  code: string | null | undefined
): boolean {
  const n = normalizeLanguageCode(code).replace(/-/g, "_");
  if (n === "en") return true;
  return n.startsWith("en_");
}

export function findActiveLanguageByCode<T extends LanguageLike>(
  activeLanguages: T[] | undefined | null,
  code: string | null | undefined
): T | undefined {
  if (!activeLanguages?.length || !normalizeLanguageCode(code)) {
    return undefined;
  }
  return activeLanguages.find((lang) =>
    languageCodesEqual(languageCodeFromActiveLanguage(lang), code)
  );
}
